import type { CaptainProfile } from '~/shared/types/captainProfile.js'
import type { Member } from '~/shared/types/member.js'

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

/**
 * A captain's member document as /api/subkrewes/captains serves it: name,
 * SubKrewe and dues standing for everyone, contact details only when
 * `withContact` (the caller is in the captain's SubKrewe, or may read every
 * member anyway). Nothing else in the document leaves.
 */
export function toCaptainProfile(
  id: string,
  data: Record<string, unknown>,
  opts: { paradeYear: string, withContact: boolean },
): CaptainProfile {
  const firstName = stringOrUndefined(data.first_name)
  const lastName = stringOrUndefined(data.last_name)
  const subkrewe = data.subkrewe as { id?: unknown } | null | undefined
  const purchases = data.purchases as Record<string, { membership?: unknown }> | undefined
  const membership = purchases?.[opts.paradeYear]?.membership

  const profile: CaptainProfile = {
    id,
    name: stringOrUndefined(data.name) ?? [firstName, lastName].filter(Boolean).join(' '),
    first_name: firstName,
    last_name: lastName,
    subkreweId: subkrewe && typeof subkrewe.id === 'string' ? subkrewe.id : null,
    lifetime: data.lifetime === true,
    duesPaid: Array.isArray(membership) && membership.some(p =>
      typeof (p as { status?: unknown })?.status === 'string'
      && (p as { status: string }).status.toLowerCase() === 'completed'),
  }

  if (opts.withContact) {
    const hidePhone = data.hide_phone === true
    profile.email = stringOrUndefined(data.email)
    profile.phone = hidePhone ? undefined : stringOrUndefined(data.phone)
    profile.hide_phone = hidePhone
  }
  return profile
}

/**
 * The store's Member for a captain known only by profile. Dues standing is
 * carried as one completed membership purchase for the parade year, which is
 * all the dues logic (and so isSubKreweActive) reads; its date isn't shared.
 */
export function captainProfileToMember(profile: CaptainProfile, paradeYear: string): Member {
  const now = new Date()
  return {
    id: profile.id,
    name: profile.name,
    first_name: profile.first_name,
    last_name: profile.last_name,
    email: profile.email ?? '',
    phone: profile.phone,
    hide_phone: profile.hide_phone,
    member_type: 'captain',
    lifetime: profile.lifetime,
    purchases: profile.duesPaid
      ? { [paradeYear]: { membership: [{ orderId: '', purchasedAt: new Date(0), status: 'completed' }] } }
      : undefined,
    subkrewe: null,
    created_at: now,
    updated_at: now,
  }
}
